export const DEFAULT_IMAGE =
  "https://cdn.outschool.com/outschool-filestack-migration/0wotKfsgR4iMbewMMtEJ_Placeholder.png";
export const DEFAULT_PROFILE_IMAGE =
  "https://cdn.outschool.com/outschool-filestack-migration/V7Iy0eR9CdpoHexqhqQj_avatar.png";

type ResizeOptions = {
  height?: number;
  h?: number;
  width?: number;
  w?: number;
  fit?: string;
};

export function imageUrl(
  url?: string | null,
  options?: ResizeOptions,
  format: FilestackImageFormats = "jpg"
) {
  if (
    url?.startsWith("https://cdn.filestackcontent.com/") ||
    url?.startsWith("https://www.filepicker.io/")
  ) {
    return fsImageUrl(url, options, format);
  } else if (url?.startsWith("https://cdn.outschool")) {
    return osImageUrl(url, options, format);
  }

  return url ?? "invalid url";
}

// eventually we will want to just delete this version after we complete filestack migration
function filestackResizeString(options: ResizeOptions = {}) {
  const unroundedHeight = options.height ?? options.h;
  const unroundedWidth = options.width ?? options.w;
  const height = unroundedHeight ? Math.round(unroundedHeight) : false;
  const width = unroundedWidth ? Math.round(unroundedWidth) : false;

  const defaultResizeOptions = {
    fit: "crop",
  };

  const { w: _w, h: _h, ...optionsWithoutDimensions } = options;
  return Object.entries({
    ...defaultResizeOptions,
    ...optionsWithoutDimensions,
    width,
    height,
  })
    .map(([key, val]) => val && `${key}:${val}`)
    .filter(x => x)
    .sort()
    .join(",");
}

function resizeParams(options: ResizeOptions = {}) {
  const unroundedHeight = options.height ?? options.h;
  const unroundedWidth = options.width ?? options.w;
  const height = unroundedHeight ? Math.round(unroundedHeight) : false;
  const width = unroundedWidth ? Math.round(unroundedWidth) : false;

  const { w: _w, h: _h, ...optionsWithoutDimensions } = options;
  return Object.entries({
    ...optionsWithoutDimensions,
    width,
    height,
  })
    .map(([key, val]) => val && `${key}=${val}`)
    .filter(x => x)
    .sort();
}

type FilestackImageFormats = "jpg" | "pjpg" | "png" | "svg" | "webp";

export function osImageUrl(
  url?: string | null,
  options?: ResizeOptions,
  format: FilestackImageFormats = "jpg"
) {
  if (!url) {
    // This is the default "missing photo" image we show
    url = DEFAULT_IMAGE;
    format = "png";
  }

  /*
    /images is not an actual path to s3 bucket, but something that 
    is used to trigger the image lambda. Once lambda reads this,
    it will then strip the /images to get the right path to the
    original image used for transformation

    if original was
    https://cdn.outschool.com/<s3path>
    then we want this to be
    https://cdn.outschool.com/images/<s3path>
  */
  let originalUrl: URL;
  try {
    originalUrl = new URL(url); // parses host, pathname, etc.
  } catch (error) {
    // URL is invalid
    console.warn(`Invalid URL provided to transformation: ${url}`, error);
    return "invalid url"; // Return empty url --> will indicate that there is a problem so we can actually fix it
  }
  originalUrl.pathname = `/images${originalUrl.pathname}`;
  const osCdnUrl = originalUrl.toString();

  const resize =
    options && (options.width || options.height || options.w || options.h)
      ? resizeParams(options)
      : [];

  const transformParams = [
    ...resize,
    // strip:true removes metadata information from the photo
    "quality=80",
    "strip=true",
    `format=${format}`,
  ];

  return `${osCdnUrl}?${transformParams.join("&")}`;
}

export function fsImageUrl(
  url?: string | null,
  options?: ResizeOptions,
  format: FilestackImageFormats = "jpg"
) {
  let fileStackUrl =
    "https://process.filepicker.io/APHE465sSbqvbOIStdwTyz/rotate=deg:exif";

  if (!url) {
    // This is the default "missing photo" image we show
    url = DEFAULT_IMAGE;
    format = "png";
  }

  if (options && (options.width || options.height || options.w || options.h)) {
    const resize = filestackResizeString(options);
    fileStackUrl += `/resize=${resize}`;
  }

  // strip:true removes metadata information from the photo
  fileStackUrl += `/output=quality:80,compress:true,strip:true,format:${format}/cache=expiry:max/${url}`;

  return fileStackUrl;
}

export function videoThumbnailUrl(
  url: string,
  options: ResizeOptions,
  format: FilestackImageFormats = "jpg"
) {
  const resize = filestackResizeString(options);
  return `https://process.filepicker.io/APHE465sSbqvbOIStdwTyz/rotate=deg:exif/resize=${resize}/modulate=brightness:60/watermark=file:z90DIZ8PQmmN0DTt0LQG,size:60/output=quality:80,compress:true,strip:true,format:${format}/cache=expiry:max/${url}`;
}

// Currently using: 125 x 125 full size, 60x60 in featured cards and header (shown as 30x30)
export function headshotImageUrl(
  url: string | undefined | null,
  size: number = 125
) {
  return url
    ? imageUrl(url, { w: size, h: size, fit: "crop" })
    : imageUrl(DEFAULT_PROFILE_IMAGE, { w: size, h: size, fit: "crop" }, "png");
}

export function filestackPreviewUrl(fileUrl: string) {
  return `https://process.filestackapi.com/APHE465sSbqvbOIStdwTyz/output=format:pdf/${fileUrl}`;
}

export const HOMEPAGE_PREVIEW_IMAGE =
  "https://cdn.outschool.com/outschool-filestack-migration/amvs1rulSqCuHsw3wm1a_temp.png";

/**
 * s3 path stored in db has either migration bucket (outschool-filestack-migration)
 * prefix or plain path (for outschool-filestack-general bucket)
 */
export function getCdnMediaPath(s3path: string, cdn: string) {
  let pathWithBucket = "";
  if (s3path.startsWith("outschool-filestack-migration/")) {
    // remove outschool-filestack-migration from the s3 path, encode the rest and join again
    const s3Path = s3path.replace("outschool-filestack-migration/", "");
    pathWithBucket = `outschool-filestack-migration/${encodeURIComponent(
      s3Path
    )}`;
  } else {
    pathWithBucket = `outschool-filestack-general/${encodeURIComponent(
      s3path
    )}`;
  }
  return `https://${cdn}/${pathWithBucket}`;
}
